import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

type NavLink = {
  label: string;
  href?: string;
  children?: { label: string; href: string }[];
  disabled?: boolean;
};

const navLinks: NavLink[] = [
  { label: "Home", href: "/" },
  {
    label: "About",
    children: [
      { label: "About AAMECC", href: "/about" },
      { label: "Country Chapters", href: "/chapters" },
      { label: "Partners & Members", href: "/partners" },
    ],
  },
  {
    label: "Marketplace",
    children: [
      { label: "Products", href: "/marketplace/products" },
      { label: "Services", href: "/marketplace/services" },
    ],
  },
  { label: "Events", href: "/events" },
  { label: "Awards", href: "/awards" },
  { label: "Fundraise", href: "/crowdfunding" },
  { label: "Membership", href: "/register" },
  { label: "Contact", href: "/contact" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const isActive = (href: string) => location.pathname === href;

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate("/login");
  };

  const getDashboardLink = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "vendor") return "/merchant/dashboard";
    if (user.role === "investor") return "/investor";
    if (user.role === "employer") return "/employer/dashboard";
    return "/dashboard";
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto flex items-center gap-4 h-16 px-4">
        <Link to="/" className="flex items-center shrink-0">
          <img src="/logo/logo_web.webp" alt="AAMECC" width={128} height={48} fetchPriority="high" decoding="async" className="h-12 w-auto object-contain" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.disabled ? (
              <span
                key={link.label}
                title="Coming soon"
                aria-disabled="true"
                className="px-3 py-2 text-sm text-gray-300 cursor-not-allowed select-none"
              >
                {link.label}
              </span>
            ) : link.children ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(link.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button className="px-3 py-2 text-sm text-black hover:text-gold transition-colors flex items-center gap-1">
                  {link.label}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {openDropdown === link.label && (
                  <div className="absolute top-full left-0 bg-white border border-gray-200 min-w-[200px] py-2 shadow-xl rounded-lg">
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="block px-4 py-2 text-sm text-gray-700 hover:text-gold hover:bg-gray-50 transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.href}
                to={link.href!}
                className={`px-3 py-2 text-sm transition-colors ${
                  isActive(link.href!)
                    ? "text-gold font-medium"
                    : "text-black hover:text-gold"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden md:block flex-1" />

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          {isAuthenticated && user ? (
            <div
              className="relative"
              onMouseEnter={() => setUserMenuOpen(true)}
              onMouseLeave={() => setUserMenuOpen(false)}
            >
              <button type="button" aria-label="Open user menu" aria-expanded={userMenuOpen} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                  <span className="text-gold text-xs font-bold">
                    {user.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </span>
                </div>
                <div className="text-left hidden xl:block">
                  <p className="text-gray-900 text-sm font-medium leading-tight">{user.name}</p>
                  <p className="text-gray-500 text-[11px] capitalize">{user.role}</p>
                </div>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>
              {userMenuOpen && (
                <div className="absolute top-full right-0 bg-white border border-gray-200 min-w-[200px] py-2 shadow-xl rounded-lg">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-gray-900 text-sm font-medium">{user.name}</p>
                    <p className="text-gray-500 text-xs">{user.email}</p>
                  </div>
                  <Link
                    to={getDashboardLink()}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gold hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    Dashboard
                  </Link>
                  <Link
                    to={
                      user.role === "vendor" ? "/merchant/profile" :
                      user.role === "investor" ? "/investor/profile" :
                      user.role === "admin" ? "/admin" :
                      user.role === "employer" ? "/employer/profile" :
                      "/dashboard/profile"
                    }
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gold hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:text-red-600 hover:bg-gray-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="px-4 py-2 text-sm text-black hover:text-gold transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 text-sm bg-gold text-gold-foreground font-medium hover:opacity-90 transition-opacity rounded-md"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile Toggle */}
        <button
          type="button"
          className="lg:hidden text-gray-700 ml-auto"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div id="mobile-nav" className="lg:hidden bg-white border-t border-gray-200">
          <div className="container mx-auto px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.disabled ? (
                <div
                  key={link.label}
                  aria-disabled="true"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 cursor-not-allowed select-none"
                >
                  {link.label}
                  <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">Soon</span>
                </div>
              ) : link.children ? (
                <div key={link.label}>
                  <div className="px-3 py-2 text-sm text-gray-400 font-medium uppercase tracking-wider">
                    {link.label}
                  </div>
                  {link.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="block px-6 py-2 text-sm text-gray-700 hover:text-gold"
                      onClick={() => setMobileOpen(false)}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href!}
                  className="block px-3 py-2 text-sm text-gray-700 hover:text-gold"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )}

            <div className="pt-4 border-t border-gray-200">
              {isAuthenticated && user ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 px-3 py-2">
                    <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                      <span className="text-gold text-xs font-bold">
                        {user.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-900 text-sm font-medium">{user.name}</p>
                      <p className="text-gray-500 text-xs capitalize">{user.role}</p>
                    </div>
                  </div>
                  <Link
                    to={getDashboardLink()}
                    className="block px-3 py-2 text-sm text-gray-700 hover:text-gold"
                    onClick={() => setMobileOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm text-red-500 hover:text-red-600"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="flex gap-3">
                  <Link
                    to="/login"
                    className="flex-1 text-center px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-md"
                    onClick={() => setMobileOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="flex-1 text-center px-4 py-2 text-sm bg-gold text-gold-foreground font-medium rounded-md"
                    onClick={() => setMobileOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
